import React, { useEffect, useState } from "react";
import {
  FormControl,
  TextField,
  Typography,
  Grid,
  Container,
  Button,
  Box,
  FormHelperText,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import * as Yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import useShopifyApi, { AddToCartItem } from "../../hooks/useShopifyApi";
import theme from "../../theme";
import { IDesign } from "../../types";
import useWebsiteApi from "../../hooks/useWebsiteApi";

const ReorderSchema = Yup.object().shape({
  orderNumber: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  email: Yup.string().email("Invalid email").required("Required"),
});

const App: React.FC<{ cartPath: string }> = (props) => {
  const { t } = useTranslation("Reorder");
  const shopifyApi = useShopifyApi();
  const websiteApi = useWebsiteApi();
  const [isLoading, setIsLoading] = useState(false);

  const {
    handleSubmit,
    getValues,
    formState: { errors, isDirty, isValid, isSubmitting },
    control,
  } = useForm<{ email: string; orderNumber: string }>({
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: yupResolver(ReorderSchema),
  });

  const onSubmit = async ({ email, orderNumber }) => {
    setIsLoading(true);

    let orderResp: null | { items: any[] } = null;

    try {
      orderResp = await websiteApi.retrieveOrders(
        email,
        orderNumber,
        window.si.storeHash
      );
    } catch (err) {
      setIsLoading(false);
      enqueueSnackbar("We could not find your order", {
        variant: "error",
      });
      return;
    }

    enqueueSnackbar("Adding items your cart", {
      variant: "success",
    });

    const { items } = orderResp;

    if (!items) {
      return;
    }

    try {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const design: IDesign = {
          ...item?.properties,
          width: item.properties.width.replace(/mm|cm|in/, ""),
          height: item.properties.height.replace(/mm|cm|in/, ""),
          unit: "mm",
          materialSku: item?.properties?.material,
          productSku: item.productSku,
          quantity: item.properties.quantity,
        };

        await shopifyApi.addToCart(design, []);
      }

      window.location.pathname = props.cartPath;
    } catch (err) {
      enqueueSnackbar("Error while setting up cart", {
        variant: "error",
      });
    }

    setIsLoading(false);
  };

  return (
    <Container sx={{ pt: "20px", display: "flex", justifyContent: "center" }}>
      <Box
        sx={{
          border: `1px solid ${theme.colors.greyPlaceholder}`,
          borderRadius: 2,
          width: { xs: "100%", md: "400px" },
          marign: "0 auto",
          display: "flex",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Box>
          {/* <Typography variant="h2">{t("pageTitle")}</Typography> */}
          <Typography variant="h2">Reorder</Typography>
          <Typography
            variant="body2"
            sx={{
              marginTop: 1,
              marginBottom: 3,
              color: theme.colors.greyDark,
            }}
          >
            {/* {t("subtitle")} */}
            Enter your details here to retrieve your past order
          </Typography>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container width="100%" rowSpacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Controller
                    control={control}
                    name="email"
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        error={Boolean(errors.email)}
                        fullWidth
                        required
                        type="email"
                        datatype="email"
                        variant="outlined"
                        // placeholder={t("emailPlaceholder") || ""}
                        placeholder="Email"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Controller
                    control={control}
                    name="orderNumber"
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        error={Boolean(errors.orderNumber)}
                        required
                        variant="outlined"
                        // placeholder={t("orderNumberPlaceholder") || ""}
                        placeholder="Order Number, e.g. SA51231"
                      />
                    )}
                  />
                  <FormHelperText>
                    Starts with SI, SM or SA. e.g. <i>SA51231</i>
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button
                  disabled={!isValid || !isDirty || isLoading}
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ marginTop: 2, marginBottom: 2 }}
                  fullWidth
                >
                  {/* {isSubmitting ? "Loading..." : t("submitButtonText")} */}
                  {isLoading ? "Loading..." : "Retrieve order"}
                </Button>
              </Grid>
            </Grid>
          </form>
          <Typography component="div"></Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default App;
